/**
 * Mask Santa Fe — related products
 * ---------------------------------------------------------------
 * Three entry points:
 *
 *   initPDP()
 *     The infinite-scroll "More to explore" section at the bottom
 *     of the full product page. This DOES its own scoring/rendering,
 *     delegated to window.MaskTracker.scoreProducts(candidates) —
 *     cross-visitor popularity, this visitor's own size/decade/style
 *     profile, low-stock urgency, freshness. Candidates come from
 *     Shopify's native recommendations endpoint + a same-product-type
 *     search fallback for pages beyond the first.
 *
 *   initQuickShopModals()
 *     The quick-shop popup's recommendations are NOT a separate
 *     build — they're the exact same native <product-recommendations>
 *     carousel used on the real product page (see
 *     snippets/product-recommendations-native.liquid), kept inert in
 *     a <template> tag until the popup for that specific product is
 *     opened, then cloned into a live target div. There's one hidden
 *     <template> per product card rendered server-side, so cloning
 *     only on open (rather than at page load) avoids firing a
 *     recommendations fetch for every card on every collection page.
 *
 * KNOWN LIMITATION (initPDP only): layout/theme.liquid confirms
 * decade/style live in product.metafields.custom.decade / .style,
 * NOT tags. The lightweight endpoints used here for candidates
 * (/recommendations/products.json, /search.json) don't return
 * metafields at all, so decade/style can't be read for these
 * candidates client-side — extractDecade()/extractStyle() return
 * null and MaskTracker.scoreProducts() simply skips that part of its
 * scoring for them (size matching, cross-visitor score, inventory,
 * and freshness still apply normally). Getting real decade/style
 * matching here would need a small server-side lookup (e.g. a
 * metafields endpoint behind the mask-activity-tracker app proxy) —
 * ask if you want that built.
 *
 *   enhanceNativeRecommendations()
 *     The native "You may also like" carousel (sections/product-
 *     recommendations.liquid's <product-recommendations> custom
 *     element) normally uses only Shopify's own recommendation
 *     algorithm — no tracking signal at all. This does NOT touch
 *     that component's own fetch/render (which we don't control and
 *     don't fully know the internals of); it waits for the component
 *     to finish rendering its own cards, independently fetches the
 *     same candidate pool via fetchShopifyRecs(), scores it with
 *     MaskTracker.scoreProducts(), and reorders the already-rendered
 *     DOM cards to match — a re-sort layered on top of Shopify's
 *     normal behavior, not a replacement for it. Runs for every
 *     <product-recommendations> element on the page, including the
 *     one cloned into an opened quick-shop modal.
 */
(function (window) {
  const PAGE_SIZE = 8;
  const SIZE_TAG_RE = /^size[-_]?\s*/i;

  // ---- shape candidates for MaskTracker.scoreProducts() ---------------

  function extractSizes(tags) {
    return (tags || [])
      .filter((t) => SIZE_TAG_RE.test(t))
      .map((t) => t.replace(SIZE_TAG_RE, '').trim().toUpperCase());
  }

  function extractDecade(_tags) {
    // Not derivable from these lightweight endpoints — see header note.
    return null;
  }

  function extractStyle(_tags) {
    // Not derivable from these lightweight endpoints — see header note.
    return null;
  }

  function toCandidate(rawProduct) {
    return {
      id: rawProduct.id,
      sizes: extractSizes(rawProduct.tags),
      decade: extractDecade(rawProduct.tags),
      style: extractStyle(rawProduct.tags),
      inventory: rawProduct.total_inventory ?? rawProduct.inventory ?? null,
      created_at: rawProduct.created_at || rawProduct.published_at || null,
      // keep the raw fields around for rendering after scoring
      _raw: rawProduct,
    };
  }

  // ---- candidate sourcing (initPDP only) --------------------------------

  async function fetchShopifyRecs(productId, limit) {
    const res = await fetch(
      `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`
    );
    if (!res.ok) return [];
    const data = await res.json();
    return data.products || [];
  }

  async function fetchMoreByType(productType, excludeIds, page) {
    const q = encodeURIComponent(`product_type:${productType}`);
    const res = await fetch(`/search.json?type=product&q=${q}&page=${page}`);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.resources?.results?.products || []).filter(
      (p) => !excludeIds.has(String(p.id))
    );
  }

  // ---- rendering (initPDP only) — mirrors product-grid-item.liquid
  //      closely enough that MaskTracker's existing click tracking and
  //      renderCollectionBadges() pick these cards up automatically ----

  function renderCard(raw) {
    const el = document.createElement('div');
    el.className = 'grid__item grid-product medium-up--one-quarter small--one-half';
    el.dataset.productGrid = '';
    el.dataset.id = raw.id;
    el.dataset.qty = raw.total_inventory ?? raw.inventory ?? 0;

    const price = raw.price ? `$${(raw.price / 100).toFixed(2)}` : '';
    const img = raw.featured_image || raw.images?.[0] || '';

    el.innerHTML = `
      <div class="grid-product__content">
        <a href="/products/${raw.handle}" class="grid-product__link" data-product-id="${raw.id}">
          <div class="image-wrap">
            <img src="${img}" alt="${raw.title}" loading="lazy" class="grid-product__image">
          </div>
          <div class="grid-product__meta">
            <div class="grid-product__title">${raw.title}</div>
            <div class="grid-product__price">${price}</div>
          </div>
        </a>
      </div>
    `;
    return el;
  }

  // ---- PDP: infinite scroll -------------------------------------------

  async function initPDP() {
    const root = document.querySelector('[data-mask-related-scope="page"]');
    if (!root || !window.MaskTracker) return;

    const anchor = JSON.parse(root.dataset.product); // { id, product_type }
    const grid = root.querySelector('[data-mask-related-grid]');
    const sentinel = root.querySelector('[data-mask-related-sentinel]');
    const shownIds = new Set([String(anchor.id)]);
    let page = 2; // page 1 = Shopify's native recommendations
    let loading = false;
    let exhausted = false;

    const seed = await fetchShopifyRecs(anchor.id, 20);
    await scoreAndRender(seed);

    async function scoreAndRender(rawList) {
      const fresh = rawList.filter((p) => !shownIds.has(String(p.id)));
      if (!fresh.length) return;
      const candidates = fresh.map(toCandidate);
      const ranked = await window.MaskTracker.scoreProducts(candidates);
      ranked.slice(0, PAGE_SIZE).forEach(({ product }) => {
        shownIds.add(String(product.id));
        grid.appendChild(renderCard(product._raw));
      });
      // re-run badge logic so the new cards get urgency/shipping badges
      window.MaskTracker.renderCollectionBadges?.();
      window.MaskTracker.renderShippingBadges?.();
    }

    async function loadMore() {
      if (loading || exhausted) return;
      loading = true;
      const more = await fetchMoreByType(anchor.product_type, shownIds, page);
      if (!more.length) {
        exhausted = true;
      } else {
        page += 1;
        await scoreAndRender(more);
      }
      loading = false;
    }

    new IntersectionObserver(
      (entries) => entries[0].isIntersecting && loadMore(),
      { rootMargin: '400px' }
    ).observe(sentinel);
  }

  // ---- Quick-shop modals: clone the inert native-recommendations
  //      <template> into its target div the first time that specific
  //      modal is opened. No custom fetch/scoring here at all — once
  //      cloned, the <product-recommendations> custom element (already
  //      defined by the theme's own JS) takes over exactly as it does
  //      on the real product page. --------------------------------------

  function cloneNativeRecsFor(id) {
    const template = document.querySelector(`template[data-mask-native-recs="${id}"]`);
    const target = document.querySelector(`[data-mask-native-recs-target="${id}"]`);
    if (!template || !target || target.dataset.mskInited) return;
    target.dataset.mskInited = '1';
    target.appendChild(template.content.cloneNode(true));
    // the clone just added a fresh <product-recommendations> element
    // to the DOM — re-scan so it gets enhanced too
    enhanceNativeRecommendations();
  }

  function initQuickShopModals() {
    // Two independent triggers, since we don't control (or fully know)
    // how this theme's quick-shop script opens the overlay:
    //
    // 1) Capture-phase click listener. Capture, not bubble: if the
    //    theme's own script calls stopPropagation() on this click
    //    (common, to stop the link's normal navigation once it's
    //    opened the overlay itself), a normal bubble-phase listener
    //    here would never see the event at all. Capture listeners on
    //    an ancestor always run before that can happen.
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-product-id]');
      if (link) cloneNativeRecsFor(link.dataset.productId);
    }, true);

    // 2) IntersectionObserver on each modal's outer element, watching
    //    for it actually becoming visible — catches the open however
    //    it happens (keyboard, touch, a JS path that never dispatches
    //    a click event we'd see), independent of event mechanics
    //    entirely. Works for class-toggle or transform-based reveals;
    //    only fails if the theme uses display:none right up to the
    //    open, in which case trigger 1 above should still catch it.
    const screens = document.querySelectorAll('.screen-layer--product[data-product-id]');
    if (screens.length) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) cloneNativeRecsFor(entry.target.dataset.productId);
        });
      }, { threshold: 0.1 });
      screens.forEach((el) => observer.observe(el));
    }
  }

  // ---- Native "You may also like" carousel: re-rank in place -----------
  //      Shopify's own <product-recommendations> component owns the
  //      fetch and render; this only reorders its resulting DOM cards
  //      once they exist. Never modifies which products are shown,
  //      only the order.

  function enhanceNativeRecommendations() {
    document.querySelectorAll('product-recommendations[data-product-id]').forEach((el) => {
      if (el.dataset.mskEnhanceStarted) return;
      el.dataset.mskEnhanceStarted = '1';
      pollForRealCards(el);
    });
  }

  function pollForRealCards(el, attempt = 0) {
    const container = el.querySelector('[data-product-container]');
    if (container && container.children.length > 1) {
      reorderByScore(el, container);
      return;
    }
    if (attempt >= 20) return; // ~10s at 500ms — give up quietly if it never loads
    setTimeout(() => pollForRealCards(el, attempt + 1), 500);
  }

  async function reorderByScore(el, container) {
    if (!window.MaskTracker || container.dataset.mskReordered) return;
    container.dataset.mskReordered = '1';

    const anchorId = el.dataset.productId;
    const limit = el.dataset.limit || 14;
    const seed = await fetchShopifyRecs(anchorId, limit);
    if (!seed.length) return;

    const candidates = seed.map(toCandidate);
    const ranked = await window.MaskTracker.scoreProducts(candidates);
    const order = new Map(ranked.map((r, i) => [String(r.product.id), i]));

    const cards = Array.from(container.children);
    cards.sort((a, b) => {
      const ai = order.has(a.dataset.id) ? order.get(a.dataset.id) : Infinity;
      const bi = order.has(b.dataset.id) ? order.get(b.dataset.id) : Infinity;
      return ai - bi;
    });
    cards.forEach((c) => container.appendChild(c));
  }

  window.MaskRelated = { initPDP, initQuickShopModals, enhanceNativeRecommendations, toCandidate };
})(window);
